import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import { useCelebrations } from '@/hooks/useCelebrations';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { chamarInstalacao } from '@/lib/rotaInstalacao';

interface TurnoAberto {
  pedido_id: string;
  numero_pedido: number | null;
  cliente: string | null;
  agenda: string;
  turno: string | null;
  cidade: string | null;
  consultor: string | null;
}

const JA_AVISADOS = 'instalacao.turnos.avisados';

function lerAvisados(): string[] {
  try { return JSON.parse(localStorage.getItem(JA_AVISADOS) || '[]'); } catch { return []; }
}

function guardarAvisado(chave: string) {
  try { localStorage.setItem(JA_AVISADOS, JSON.stringify([...lerAvisados(), chave].slice(-200))); } catch { /* sem armazenamento: avisa só nesta sessão */ }
}

const avisadosNaSessao = new Set<string>();

const horas = (turno: string | null) => (turno ? turno.split('–').map((h) => `${Number(h.slice(0, 2))}h`).join(' às ') : '');

/**
 * FIBRA EM INSTALAÇÃO AGORA (Douglas 30/10/2026, maquete v7): quando o turno da
 * instalação abre, o aviso aparece na tela do consultor dono, da BKO da
 * Tramitação, do supervisor da equipe, do gerente e do master, igual ao da
 * fatura e com som. Um aviso por pedido e data.
 */
export function useAvisoTurnoInstalacao() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { notification } = useCelebrations();
  const { open: abrirPedido } = usePainelPedido();

  const { data = [] } = useQuery({
    queryKey: ['instalacao-turnos-abertos', user?.id],
    enabled: !!user?.id,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    staleTime: 30_000,
    retry: false,
    queryFn: () => chamarInstalacao<TurnoAberto[]>('turnos-abertos', 'GET'),
  });

  useEffect(() => {
    const avisados = new Set(lerAvisados());
    for (const t of data) {
      const chave = `${t.pedido_id}:${t.agenda}`;
      if (avisados.has(chave) || avisadosNaSessao.has(chave)) continue;
      avisadosNaSessao.add(chave);
      guardarAvisado(chave);
      notification();
      const detalhe = [`hoje, das ${horas(t.turno)}`, t.cidade, t.consultor?.split(' ').slice(0, 2).join(' ')].filter(Boolean).join(' · ');
      toast({
        title: React.createElement('div', { className: 'text-[13px] font-semibold leading-tight truncate text-white' },
          `Fibra em instalação agora — ${t.cliente || `pedido ${t.numero_pedido ?? ''}`}`) as never,
        description: React.createElement('div', { className: 'text-xs leading-tight truncate text-white/65' }, detalhe) as never,
        duration: 12000,
        className: 'w-full max-w-[440px] rounded-xl border border-white/[0.07] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] py-2.5 pl-3.5 pr-2 shadow-md gap-2 overflow-hidden [&>div]:min-w-0 [&_button[toast-close]]:text-white/70',
        action: React.createElement(ToastAction as never, {
          altText: 'Acompanhar',
          className: 'bg-white text-primary hover:bg-white/90 border-0 rounded-md h-7 px-3 text-xs font-semibold shrink-0 whitespace-nowrap',
          onClick: () => abrirPedido(t.pedido_id, 'visao360'),
        }, 'Acompanhar') as unknown as ToastActionElement,
      });
    }
  }, [data, toast, notification, abrirPedido]);
}
